import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, User, Star, Ban, CreditCard, Shield
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const { getAuthHeaders } = useAdmin();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/customers?limit=50`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const toggleVIP = async (customerId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/customers/${customerId}/vip?is_vip=${!currentStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchCustomers();
    } catch (error) {
      console.error('Failed to update VIP status:', error);
    }
  };

  const toggleBan = async (customerId, currentStatus) => {
    if (!currentStatus && !window.confirm('Are you sure you want to ban this customer?')) return;
    
    try {
      await axios.put(
        `${API}/admin/customers/${customerId}/ban?is_banned=${!currentStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchCustomers();
    } catch (error) {
      console.error('Failed to update ban status:', error);
    }
  };

  const addStoreCredit = async (customerId) => {
    const amount = prompt('Enter store credit amount ($):');
    if (!amount || isNaN(amount)) return;
    
    try {
      await axios.post(
        `${API}/admin/customers/${customerId}/store-credit?amount=${parseFloat(amount)}&reason=Admin%20credit`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchCustomers();
    } catch (error) {
      console.error('Failed to add store credit:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getRiskColor = (risk) => {
    const colors = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700'
    };
    return colors[risk] || colors.low;
  };

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-customers-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black text-stone-900">Customers</h1>
          <p className="text-stone-500">Manage customer accounts and profiles</p>
        </div>

        {/* Search */}
        <Card className="mb-6 border-0 shadow-lg">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Search by email or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search"
                />
              </div>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card className="border-0 shadow-lg" data-testid="customers-table">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <User className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                <p>No customers found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Orders</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Total Spent</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Store Credit</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {customers.map((customer) => (
                      <tr key={customer._id} className="hover:bg-stone-50" data-testid={`customer-row-${customer._id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                              <span className="font-bold text-amber-700">
                                {customer.email?.[0]?.toUpperCase() || 'U'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-stone-900">
                                {customer.first_name} {customer.last_name}
                                {customer.is_vip && <Star className="inline h-4 w-4 text-amber-500 ml-1" />}
                              </p>
                              <p className="text-sm text-stone-500">{customer.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-stone-600">{customer.total_orders || 0}</td>
                        <td className="px-6 py-4 font-semibold">{formatCurrency(customer.total_spent)}</td>
                        <td className="px-6 py-4 text-green-600">{formatCurrency(customer.store_credit)}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(customer.risk_profile)}`}>
                              {customer.risk_profile || 'low'} risk
                            </span>
                            {customer.is_banned && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                                Banned
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              title={customer.is_vip ? 'Remove VIP' : 'Make VIP'}
                              onClick={() => toggleVIP(customer._id, customer.is_vip)}
                            >
                              <Star className={`h-4 w-4 ${customer.is_vip ? 'text-amber-500 fill-amber-500' : 'text-stone-400'}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Add Store Credit"
                              onClick={() => addStoreCredit(customer._id)}
                            >
                              <CreditCard className="h-4 w-4 text-green-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={customer.is_banned ? 'Unban' : 'Ban'}
                              onClick={() => toggleBan(customer._id, customer.is_banned)}
                            >
                              <Ban className={`h-4 w-4 ${customer.is_banned ? 'text-red-500' : 'text-stone-400'}`} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCustomers;
